// src/components/MarketingStudio.jsx

import React, { useState, useEffect } from 'react';
import { 
  Upload, Image as ImageIcon, Video, Clock, CheckCircle, 
  AlertCircle, Download, ArrowLeft, Plus, X, 
  Sparkles, Wand2, Star, Layers, ZoomIn, Trash2, Film, Edit3
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../config';
import CreditsPurchase from './CreditsPurchase';


const MarketingStudio = ({ onBack }) => {
  
  // ========== TABS ==========
  const [activeTab, setActiveTab] = useState('create-image');
  
  // ========== CRÉATION IMAGE MARKETING ==========
  const [productImage, setProductImage] = useState(null);
  const [productImagePreview, setProductImagePreview] = useState(null);
  const [modelImage, setModelImage] = useState(null);
  const [modelImagePreview, setModelImagePreview] = useState(null);
  const [showPerson, setShowPerson] = useState(true);
  const [view, setView] = useState('studio');
  
  const [marketingPrompt, setMarketingPrompt] = useState('');
  const [productName, setProductName] = useState('');
  const [price, setPrice] = useState('');
  const [slogan, setSlogan] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [productCategory, setProductCategory] = useState('auto');
  const [currentVideoImageUrl, setCurrentVideoImageUrl] = useState(null);
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [useAdditionalPrompt, setUseAdditionalPrompt] = useState(false);
  const [videoConfig, setVideoConfig] = useState({
    promptMode: 'auto',
    visualStyle: 'luxury_gold',
    cameraMovement: 'arc_180',
    voiceType: 'none',
    voiceText: '',
    customPrompt: ''
  });
  
  // ========== MODAL ZOOM IMAGE ==========
  const [zoomedImage, setZoomedImage] = useState(null);
  
  // ========== MODAL PROMPT VIDÉO ==========
  const [showVideoPromptModal, setShowVideoPromptModal] = useState(false);
  const [pendingVideoImageUrl, setPendingVideoImageUrl] = useState(null);
  
  // ========== ÉTATS GÉNÉRATION ==========
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageJobId, setImageJobId] = useState(null);
  const [imageJobStatus, setImageJobStatus] = useState(null);
  
  const [userQuotas, setUserQuotas] = useState({
    max_images: 0,
    max_videos: 0,
    used_images: 0,
    used_videos: 0,
    remaining_images: 0,
    remaining_videos: 0
  });

  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [videoGenerationError, setVideoGenerationError] = useState(null);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [videoJobId, setVideoJobId] = useState(null);
  const [videoJobStatus, setVideoJobStatus] = useState(null);
  const [selectedNanoBananaImage, setSelectedNanoBananaImage] = useState(null);
  
  const [error, setError] = useState(null);
  
  // ========== MES CRÉATIONS ==========
  const [myImages, setMyImages] = useState([]);
  const [myVideos, setMyVideos] = useState([]);
  const [loadingCreations, setLoadingCreations] = useState(false);
  const [limits, setLimits] = useState({ max_images: 50, max_videos: 25 });
  
  const token = localStorage.getItem('auth_token');
  
  // ========== POLLING STATUS ==========
  
  useEffect(() => {
    checkUserQuotas();
  }, []);

  useEffect(() => {
    if (imageJobId) {
      const interval = setInterval(() => pollImageStatus(imageJobId), 3000);
      return () => clearInterval(interval);
    }
  }, [imageJobId]);
  
  useEffect(() => {
    if (videoJobId) {
      const interval = setInterval(() => pollVideoStatus(videoJobId), 5000);
      return () => clearInterval(interval);
    }
  }, [videoJobId]);
  
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes progress {
        0% { transform: translateX(-100%); }
        50% { transform: translateX(0%); }
        100% { transform: translateX(100%); }
      }
      
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const checkUserQuotas = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/marketing-studio/quotas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('📊 Quotas reçus:', response.data);
      
      if (response.data.success && response.data.has_subscription) {
        setUserQuotas(response.data.quotas);
        console.log('✅ Quotas chargés:', response.data.quotas);
      } else {
        setUserQuotas({
          max_images: 0,
          max_videos: 0,
          used_images: 0,
          used_videos: 0,
          remaining_images: 0,
          remaining_videos: 0
        });
        console.log('⚠️ Pas de subscription active');
      }
    } catch (err) {
      console.error('❌ Erreur quotas:', err);
      setError('Impossible de charger les quotas');
    }
  };


  const pollImageStatus = async (jobId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/nanobanana/status/${jobId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setImageJobStatus(response.data);
      
      if (response.data.status === 'completed') {
        setGeneratedImage(response.data.image_url);
        setGeneratingImage(false);
        setImageJobId(null);
        
        await checkUserQuotas();
        
        if (activeTab === 'my-creations') {
          loadMyCreations();
        }
        
        console.log('✅ Image générée avec succès');
        
      } else if (response.data.status === 'failed') {
        setError(translateError(response.data.error) || 'Génération échouée');
        setGeneratingImage(false);
        setImageJobId(null);
      }
    } catch (err) {
      console.error('❌ Erreur polling:', err);
    }
  };
  
  const pollVideoStatus = async (jobId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/veo/status/${jobId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setVideoJobStatus(response.data);
      
      if (response.data.status === 'completed') {
        setGeneratedVideo(response.data.video_url);
        setGeneratingVideo(false);
        setCurrentVideoImageUrl(null);
        setVideoJobId(null);
        
        await checkUserQuotas();
        
        if (activeTab === 'my-creations') {
          loadMyCreations();
        }
        
        console.log('✅ Vidéo générée avec succès');
        
      } else if (response.data.status === 'failed') {
        setError(translateError(response.data.error) || 'Génération vidéo échouée');
        setGeneratingVideo(false);
        setCurrentVideoImageUrl(null);
        setVideoJobId(null);
      }
    } catch (err) {
      console.error('❌ Erreur polling vidéo:', err);
    }
  };
  
  // ========== TRADUCTION ERREURS ==========
  const translateError = (errorMsg) => {
    if (!errorMsg) return null;
    
    const translations = {
      'Your prompt was flagged by Website as violating content policies': 
        '⚠️ Votre prompt contient des mots interdits. Évitez: "produit", "marque", "prix", "acheter"',
      'flagged': '⚠️ Contenu refusé. Utilisez des mots neutres.',
      'content policy': '⚠️ Contenu refusé par les règles de sécurité',
      'violation': '⚠️ Votre texte viole les règles',
      'quota': '⚠️ Quota épuisé',
      'limit': '⚠️ Limite atteinte'
    };
    
    for (const [key, translation] of Object.entries(translations)) {
      if (errorMsg.toLowerCase().includes(key.toLowerCase())) {
        return translation;
      }
    }
    
    return errorMsg;
  };
  
  // ========== CHARGER CRÉATIONS ==========
  
  useEffect(() => {
    if (activeTab === 'my-creations') {
      loadMyCreations();
    }
  }, [activeTab]);
  
  const loadMyCreations = async () => {
    setLoadingCreations(true);
    try {
      const response = await axios.get(`${API_URL}/api/unified/my-creations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('📦 Créations reçues:', response.data);
      
      const allImages = response.data.images || [];
      const allVideos = response.data.videos || [];
      
      const veoVideos = allVideos.filter(v => v.type === 'veo3' || v.service === 'marketing_studio');
      
      setMyImages(allImages);
      setMyVideos(veoVideos);
      
      setLimits(response.data.limits || { max_images: 50, max_videos: 25 });
      
    } catch (err) {
      console.error('❌ Erreur chargement:', err);
    } finally {
      setLoadingCreations(false);
    }
  };
  
  // ========== HELPER: Normaliser URL ==========
  const normalizeImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return API_URL + url;
  };
  
  // ========== GESTION IMAGES ==========
  
  const handleProductImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      setError('Image trop grande (max 10MB)');
      return;
    }
    
    setProductImage(file);
    setProductImagePreview(URL.createObjectURL(file));
    setError(null);
  };
  
  const removeProductImage = () => {
    setProductImage(null);
    setProductImagePreview(null);
  };
  
  const handleModelImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      setError('Image trop grande (max 10MB)');
      return;
    }
    
    setModelImage(file);
    setModelImagePreview(URL.createObjectURL(file));
    setError(null);
  };
  
  const removeModelImage = () => {
    setModelImage(null);
    setModelImagePreview(null);
  };
  
  // ========== GÉNÉRATION IMAGE ==========
  
  const generateMarketingImage = async () => {
    if (!productImage) {
      setError('Ajoutez une image produit');
      return;
    }
    
    if (userQuotas.remaining_images <= 0) {
      setError(`⚠️ Quota images épuisé (${userQuotas.used_images}/${userQuotas.max_images}). Contactez l'administrateur.`);
      return;
    }
    
    setGeneratingImage(true);
    setError(null);
    setGeneratedImage(null);
    
    try {
      const formData = new FormData();
      
      formData.append('product_image', productImage);
      
      if (showPerson && modelImage) {
        formData.append('model_image', modelImage);
      }
      
      formData.append('show_person', showPerson.toString());
      
      let finalPrompt = marketingPrompt;
      if (!finalPrompt) {
        finalPrompt = `Professional marketing photo of ${productName || 'product'}`;
        if (price) finalPrompt += ` with price "${price}"`;
        if (slogan) finalPrompt += ` and tagline "${slogan}"`;
        if (companyName) finalPrompt += ` featuring "${companyName}" brand`;
      }
      
      if (useAdditionalPrompt && additionalPrompt.trim()) {
        finalPrompt += `. ${additionalPrompt.trim()}`;
      }
      
      formData.append('prompt', finalPrompt);
      formData.append('product_category', productCategory);
      formData.append('image_size', aspectRatio);
      formData.append('output_format', 'png');
      
      if (productName) formData.append('product_name', productName);
      if (price) formData.append('price', price);
      if (slogan) formData.append('slogan', slogan);
      if (companyName) formData.append('company_name', companyName);
      
      if (useAdditionalPrompt && additionalPrompt.trim()) {
        formData.append('additional_prompt', additionalPrompt.trim());
      }
      
      const response = await axios.post(
        `${API_URL}/api/nanobanana/generate-poster`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      console.log('✅ Job créé:', response.data);
      setImageJobId(response.data.job_id);
      
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(translateError(err.response?.data?.error) || 'Erreur génération');
      setGeneratingImage(false);
    }
  };
  
  // ========== GÉNÉRATION VIDÉO AVEC MODAL ==========
  
  const openVideoPromptModal = (imageUrl) => {
    // ✅ PASSER À L'ONGLET CRÉATION
    setActiveTab('create-image');
    
    setSelectedNanoBananaImage({ image_url: imageUrl });
    setPendingVideoImageUrl(imageUrl);
    setGeneratedImage(imageUrl);
    
    setVideoConfig({
      promptMode: 'auto',
      visualStyle: 'luxury_gold',
      cameraMovement: 'arc_180',
      voiceType: 'none',
      voiceText: '',
      customPrompt: ''
    });
    
    setVideoGenerationError(null);
    setShowVideoPromptModal(true);
    
    console.log('✅ Modal vidéo ouvert');
  };
  
  const confirmVideoGeneration = async () => {
    if (userQuotas.remaining_videos <= 0) {
      setVideoGenerationError(`⚠️ Quota vidéos épuisé (${userQuotas.used_videos}/${userQuotas.max_videos}). Contactez l'administrateur.`);
      return;
    }
    
    setGeneratingVideo(true);
    setVideoGenerationError(null);
    setShowVideoPromptModal(false);
    setCurrentVideoImageUrl(selectedNanoBananaImage?.image_url);
    
    try {
      if (videoConfig.promptMode === 'custom' && !videoConfig.customPrompt?.trim()) {
        setVideoGenerationError('Veuillez entrer un prompt personnalisé');
        setGeneratingVideo(false);
        return;
      }
      
      if (videoConfig.promptMode === 'auto' && videoConfig.voiceType !== 'none' && !videoConfig.voiceText?.trim()) {
        setVideoGenerationError('Veuillez entrer le texte à dire/chanter');
        setGeneratingVideo(false);
        return;
      }
      
      if (!selectedNanoBananaImage?.image_url) {
        setVideoGenerationError('Image source manquante');
        setGeneratingVideo(false);
        return;
      }
      
      console.log('🎬 Lancement génération vidéo Veo...');
      
      const formData = new FormData();
      formData.append('image_url', selectedNanoBananaImage.image_url);
      formData.append('video_config', JSON.stringify(videoConfig));
      formData.append('prompt_mode', videoConfig.promptMode || 'auto');
      
      if (videoConfig.promptMode === 'custom') {
        formData.append('custom_prompt', videoConfig.customPrompt.trim());
      }
      
      formData.append('voice_type', videoConfig.voiceType || 'none');
      formData.append('voice_text', videoConfig.voiceText?.trim() || '');
      
      const response = await axios.post(`${API_URL}/api/veo/generate`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('✅ Réponse Veo:', response.data);
      
      if (response.data.success) {
        setVideoJobId(response.data.job_id);
      } else {
        throw new Error(response.data.error || 'Échec génération vidéo');
      }
      
    } catch (err) {
      console.error('❌ Erreur génération vidéo:', err);
      const errorMsg = translateError(err.response?.data?.error || err.message) || 'Erreur lors de la génération';
      setError(errorMsg);
      setGeneratingVideo(false);
      setCurrentVideoImageUrl(null);
    }
  };

  // ========== SUPPRIMER IMAGE/VIDÉO ==========
  
  const deleteImage = async (img) => {
    if (!window.confirm('Supprimer cette image définitivement ?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/unified/delete-image`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          data: {
            user_folder: img.user_folder,
            filename: img.filename
          }
        }
      );
      
      console.log('✅ Image supprimée');
      loadMyCreations();
    } catch (err) {
      console.error('❌ Erreur suppression:', err);
      setError('Erreur suppression image');
    }
  };
  
  const deleteVideo = async (vid) => {
    if (!window.confirm('Supprimer cette vidéo définitivement ?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/unified/delete-video`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          data: {
            user_folder: vid.user_folder,
            filename: vid.filename
          }
        }
      );
      
      console.log('✅ Vidéo supprimée');
      loadMyCreations();
    } catch (err) {
      console.error('❌ Erreur suppression:', err);
      setError('Erreur suppression vidéo');
    }
  };
  
  // ========== RESET ==========
  
  const reset = () => {
    setProductImage(null);
    setProductImagePreview(null);
    setModelImage(null);
    setModelImagePreview(null);
    setShowPerson(true);
    setMarketingPrompt('');
    setProductName('');
    setPrice('');
    setSlogan('');
    setCompanyName('');
    setProductCategory('auto');
    setAdditionalPrompt('');
    setUseAdditionalPrompt(false);
    setGeneratedImage(null);
    setGeneratedVideo(null);
    setImageJobId(null);
    setVideoJobId(null);
    setCurrentVideoImageUrl(null);
    setError(null);
  };
  
  // ========== RENDER ==========
  if (view === 'credits') {
    return (
      <CreditsPurchase 
        onBack={() => {
          setView('dashboard'); // ou 'video_ads' / 'vtryon' / 'marketing_studio'
          // Recharger les subscriptions si nécessaire
        }} 
      />
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 pb-8">
      {/* ✅ HEADER FIXE EN HAUT */}
      <div className="sticky top-0 z-50 bg-white shadow-lg border-b-2 border-purple-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
            >
              <ArrowLeft className="w-5 h-5" />
              Retour
            </button>
            
            <div className="flex items-center gap-3">
              <Wand2 className="w-8 h-8 text-purple-600" />
              <h1 className="text-2xl font-bold text-gray-900">
                Marketing Studio TeckiA
              </h1>
              <Sparkles className="w-7 h-7 text-orange-500" />
              <button
                              onClick={() => setView('credits')}
                              className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-purple-600 hover:from-yellow-500/30 hover:to-orange-500/30 text-white border border-yellow-500/30 transition-all hover:scale-105 text-sm md:text-base group"
                            >
                              <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                              <span className="hidden sm:inline">Acheter Crédits</span>
                              <span className="sm:hidden">Crédits</span>
                            </button>
                            
            </div>
            
            <div className="w-24"></div>
          </div>
          
          {/* ✅ QUOTAS USER */}
          <div className="flex items-center justify-center gap-4">
            <div className="px-6 py-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl border-2 border-purple-300">
              <div className="flex items-center gap-3">
                <ImageIcon className="w-5 h-5 text-purple-600" />
                <div className="text-left">
                  <p className="text-xs text-purple-600 font-semibold">Images restantes</p>
                  <p className="text-lg font-bold text-purple-900">
                    {userQuotas.remaining_images} / {userQuotas.max_images}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-2 bg-gradient-to-r from-pink-100 to-orange-100 rounded-xl border-2 border-pink-300">
              <div className="flex items-center gap-3">
                <Video className="w-5 h-5 text-pink-600" />
                <div className="text-left">
                  <p className="text-xs text-pink-600 font-semibold">Vidéos restantes</p>
                  <p className="text-lg font-bold text-pink-900">
                    {userQuotas.remaining_videos} / {userQuotas.max_videos}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pt-6">
        
        {/* ✅ MODAL PROMPT VIDÉO */}
        {showVideoPromptModal && (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-gradient-to-br from-pink-600 via-purple-600 to-blue-600 rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-white flex items-center">
          <Sparkles className="w-8 h-8 mr-3" />
          Vidéo Marketing PRO
        </h2>
        <button
          onClick={() => setShowVideoPromptModal(false)}
          className="text-white hover:bg-white/20 rounded-full p-2 transition"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Mode de création */}
      <div className="mb-6">
        <label className="text-white font-bold mb-3 block">
          🎨 Mode de création
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setVideoConfig({...videoConfig, promptMode: 'auto'})}
            className={`p-4 rounded-xl border-2 transition ${
              videoConfig.promptMode === 'auto'
                ? 'border-yellow-400 bg-white/20'
                : 'border-white/30 bg-white/10 hover:bg-white/15'
            }`}
          >
            <Wand2 className={`w-6 h-6 mx-auto mb-2 ${
              videoConfig.promptMode === 'auto' ? 'text-yellow-400' : 'text-white'
            }`} />
            <p className="text-white font-bold text-sm">Auto</p>
            <p className="text-white/70 text-xs">Génération guidée</p>
          </button>

          <button
            onClick={() => setVideoConfig({...videoConfig, promptMode: 'custom'})}
            className={`p-4 rounded-xl border-2 transition ${
              videoConfig.promptMode === 'custom'
                ? 'border-yellow-400 bg-white/20'
                : 'border-white/30 bg-white/10 hover:bg-white/15'
            }`}
          >
            <Edit3 className={`w-6 h-6 mx-auto mb-2 ${
              videoConfig.promptMode === 'custom' ? 'text-yellow-400' : 'text-white'
            }`} />
            <p className="text-white font-bold text-sm">Prompt Perso</p>
            <p className="text-white/70 text-xs">Contrôle total</p>
          </button>
        </div>
      </div>

      {/* MODE AUTO */}
      {videoConfig.promptMode === 'auto' && (
        <div className="space-y-4">
          
          <div>
            <label className="text-white font-bold mb-2 block">✨ Style Visuel</label>
            <select
              value={videoConfig.visualStyle}
              onChange={(e) => setVideoConfig({...videoConfig, visualStyle: e.target.value})}
              className="w-full px-4 py-3 bg-white/10 border-2 border-white/30 rounded-xl text-pink focus:outline-none focus:border-yellow-400"
            >
              <option value="luxury_gold">🌟 Luxe Doré</option>
              <option value="modern_tech">💎 Moderne Tech</option>
              <option value="vibrant_energy">🎨 Vibrant Énergique</option>
              <option value="minimal_elegant">✨ Minimal Élégant</option>
            </select>
          </div>

        </div>
      )}


      {/* MODE CUSTOM */}
      {videoConfig.promptMode === 'custom' && (
        <div>
          <label className="text-white font-bold mb-2 block">✍️ Votre Prompt</label>
          <textarea
            value={videoConfig.customPrompt || ''}
            onChange={(e) => setVideoConfig({...videoConfig, customPrompt: e.target.value})}
            placeholder="Ex: faire que tout soit parfaitement dans un cadre magnifique, en disant: Magnifique produit"
            rows={6}
            maxLength={200}
            className="w-full px-4 py-3 bg-white/10 border-2 border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 resize-none"
          />
          <p className="text-white/60 text-xs mt-1">
            ⚠️ Évitez: "produit", "marque", "prix", "acheter" → Utilisez des mots neutres
          </p>
        </div>
      )}

      {videoGenerationError && (
        <div className="mt-4 p-3 bg-red-500/20 border border-red-400 rounded-lg text-red-200 text-sm">
          {videoGenerationError}
        </div>
      )}

      {/* Boutons */}
      <div className="flex gap-3 mt-6">
        <button
          onClick={() => setShowVideoPromptModal(false)}
          className="flex-1 px-6 py-3 bg-white/20 text-white rounded-xl hover:bg-white/30 transition font-bold"
        >
          Annuler
        </button>
        <button
          onClick={confirmVideoGeneration}
          disabled={generatingVideo || (videoConfig.promptMode === 'custom' && !videoConfig.customPrompt)}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-xl hover:from-yellow-500 hover:to-orange-600 transition font-bold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generatingVideo ? (
            <>
              <Clock className="w-5 h-5 inline mr-2 animate-spin" />
              Génération...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5 inline mr-2" />
              Générer Vidéo
            </>
          )}
        </button>
      </div>
    </div>
  </div>
)}
        
        {/* MODAL ZOOM */}
        {zoomedImage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            onClick={() => setZoomedImage(null)}
          >
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition z-10"
            >
              <X className="w-6 h-6 text-gray-800" />
            </button>
            
            <img
              src={zoomedImage}
              alt="Zoom"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}
        
        {/* TABS */}
        <div className="mb-6 flex gap-2 border-b-2 border-gray-200 bg-white rounded-t-xl px-4">
          <button
            onClick={() => setActiveTab('create-image')}
            className={`px-6 py-3 font-semibold transition-all relative ${
              activeTab === 'create-image'
                ? 'text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ImageIcon className="w-5 h-5 inline mr-2" />
            Créer Image/Vidéo
            {activeTab === 'create-image' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-t" />
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('my-creations')}
            className={`px-6 py-3 font-semibold transition-all relative ${
              activeTab === 'my-creations'
                ? 'text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Layers className="w-5 h-5 inline mr-2" />
            Mes Créations ({myImages.length} • {myVideos.length})
            {activeTab === 'my-creations' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-t" />
            )}
          </button>
        </div>
        
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          
          {/* ONGLET CRÉATION */}
          {activeTab === 'create-image' && (
            <div className="p-8">
              
              {error && (
                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-800">Erreur</p>
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* LEFT: Configuration */}
                <div className="space-y-6">
                  
                  <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <ImageIcon className="w-6 h-6 text-green-600" />
                    Configuration Affiche
                  </h3>
                  
                  {/* Image produit */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      📸 Image Produit *
                    </label>
                    
                    {productImagePreview ? (
                      <div className="relative">
                        <img
                          src={productImagePreview}
                          alt="Produit"
                          className="w-full h-64 object-cover rounded-lg border-2 border-green-300"
                        />
                        <button
                          onClick={removeProductImage}
                          className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block border-2 border-dashed border-green-300 rounded-lg p-8 text-center hover:border-green-500 transition bg-green-50">
                        <Upload className="w-12 h-12 mx-auto text-green-500 mb-3" />
                        <p className="text-sm text-gray-700 font-medium">
                          Cliquez pour ajouter l'image du produit
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          JPG, PNG, WEBP (max 10MB)
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleProductImageUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  
                  {/* Choix avec/sans personne */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      👤 Affichage Personne
                    </label>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setShowPerson(true)}
                        className={`p-4 rounded-lg border-2 transition-all text-left ${
                          showPerson
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-2xl`}>👤</span>
                          <span className={`font-bold text-sm ${showPerson ? 'text-blue-900' : 'text-gray-600'}`}>
                            Avec Personne
                          </span>
                        </div>
                        <p className="text-xs text-gray-600">
                          Modèle portant/présentant le produit
                        </p>
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => setShowPerson(false)}
                        className={`p-4 rounded-lg border-2 transition-all text-left ${
                          !showPerson
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-2xl`}>📦</span>
                          <span className={`font-bold text-sm ${!showPerson ? 'text-orange-900' : 'text-gray-600'}`}>
                            Produit Seul
                          </span>
                        </div>
                        <p className="text-xs text-gray-600">
                          Produit dans environnement pro
                        </p>
                      </button>
                    </div>
                  </div>
                  
                  {/* Image modèle (si avec personne) */}
                  {showPerson && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        👤 Photo Modèle (optionnel)
                      </label>
                      
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r-lg mb-3">
                        <p className="text-xs text-blue-800">
                          💡 <strong>Astuce PRO :</strong> Uploadez une photo de la personne que vous voulez dans l'affiche. 
                          L'IA utilisera son visage/corps exact !
                        </p>
                      </div>
                      
                      {modelImagePreview ? (
                        <div className="relative">
                          <img
                            src={modelImagePreview}
                            alt="Modèle"
                            className="w-full h-64 object-cover rounded-lg border-2 border-blue-300"
                          />
                          <button
                            onClick={removeModelImage}
                            className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <div className="absolute top-2 left-2 px-3 py-1 bg-blue-500 text-white rounded-full text-xs font-bold">
                            ✨ Modèle personnalisé
                          </div>
                        </div>
                      ) : (
                        <label className="cursor-pointer block border-2 border-dashed border-blue-300 rounded-lg p-8 text-center hover:border-blue-500 transition bg-blue-50">
                          <ImageIcon className="w-12 h-12 mx-auto text-blue-500 mb-3" />
                          <p className="text-sm text-gray-700 font-medium">
                            Ajouter une photo de modèle
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            Visage clair • JPG, PNG (max 10MB)
                          </p>
                          <p className="text-xs text-blue-600 mt-2 font-medium">
                            Vide = Modèle IA générique
                          </p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleModelImageUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  )}
                  
                  {/* Catégorie */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🏷️ Catégorie Produit *
                    </label>
                    <select
                      value={productCategory}
                      onChange={(e) => setProductCategory(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="auto">🤖 Auto-détection</option>
                      
                      <optgroup label="👕 Mode & Vêtements">
                        <option value="clothing-tshirt">T-shirt / Chemise</option>
                        <option value="clothing-pants">Pantalon / Jean</option>
                        <option value="clothing-dress">Robe / Jupe</option>
                        <option value="clothing-jacket">Veste / Manteau</option>
                        <option value="clothing-hoodie">Sweat / Hoodie</option>
                      </optgroup>
                      
                      <optgroup label="👟 Chaussures">
                        <option value="shoes-sneakers">Baskets / Sneakers</option>
                        <option value="shoes-formal">Chaussures Formelles</option>
                        <option value="shoes-heels">Talons / Escarpins</option>
                      </optgroup>
                      
                      <optgroup label="🍔 Nourriture & Boissons">
                        <option value="food-meal">Plat / Repas</option>
                        <option value="food-snack">Snack</option>
                        <option value="food-dessert">Dessert</option>
                        <option value="drink-beverage">Boisson</option>
                        <option value="drink-alcohol">Alcool</option>
                      </optgroup>
                      
                      <optgroup label="💄 Beauté & Tech">
                        <option value="beauty-skincare">Soin de peau</option>
                        <option value="beauty-makeup">Maquillage</option>
                        <option value="tech-phone">Téléphone</option>
                        <option value="tech-laptop">Ordinateur</option>
                      </optgroup>
                      
                      <optgroup label="👜 Accessoires">
                        <option value="accessory-bag">Sac</option>
                        <option value="accessory-watch">Montre</option>
                        <option value="accessory-jewelry">Bijoux</option>
                      </optgroup>
                      
                      <optgroup label="🚗 Véhicules & Autres">
                        <option value="vehicle-car">Voiture</option>
                        <option value="home-furniture">Meuble</option>
                        <option value="sport-equipment">Sport</option>
                        <option value="other">Autre</option>
                      </optgroup>
                    </select>
                  </div>
                  
                  {/* Nom produit */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🏷️ Nom Produit (optionnel)
                    </label>
                    <input
                      type="text"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder="Ex: Nike Air Max"
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* Nom entreprise */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🏢 Nom Entreprise (optionnel)
                    </label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Ex: TDI Digital"
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* Prix */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      💰 Prix (optionnel)
                    </label>
                    <input
                      type="text"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="Ex: 150 000 Ar"
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* Slogan */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      💬 Slogan (optionnel, max 20 caractères)
                    </label>
                    <input
                      type="text"
                      value={slogan}
                      onChange={(e) => setSlogan(e.target.value.slice(0, 20))}
                      placeholder="Ex: Just Do It"
                      maxLength={20}
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {slogan.length}/20 caractères
                    </p>
                  </div>
                  
                  {/* Format */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      📐 Format
                    </label>
                    <select
                      value={aspectRatio}
                      onChange={(e) => setAspectRatio(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="1:1">1:1 (Carré - Instagram)</option>
                      <option value="16:9">16:9 (Widescreen)</option>
                      <option value="9:16">9:16 (Story/Reels)</option>
                      <option value="4:3">4:3 (Paysage)</option>
                      <option value="3:4">3:4 (Portrait)</option>
                    </select>
                  </div>

                  {/* Prompt additionnel */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        ✨ Prompt Additionnel (optionnel)
                      </label>
                      
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={useAdditionalPrompt}
                          onChange={(e) => setUseAdditionalPrompt(e.target.checked)}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="ml-2 text-xs text-gray-600 font-medium">Activer</span>
                      </label>
                    </div>
                    
                    <textarea
                      value={additionalPrompt}
                      onChange={(e) => setAdditionalPrompt(e.target.value)}
                      disabled={!useAdditionalPrompt}
                      placeholder="Ex: Add dramatic lighting, golden hour atmosphere, cinematic mood..."
                      rows={3}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none ${
                        useAdditionalPrompt 
                          ? 'border-purple-200 bg-white' 
                          : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                      }`}
                    />
                    
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r-lg mt-2">
                      <p className="text-xs text-blue-800">
                        💡 <strong>Astuce :</strong> Ajoutez des instructions spécifiques sur l'ambiance, l'éclairage, 
                        les couleurs ou le style visuel.
                      </p>
                    </div>
                  </div>

                  {/* Bouton générer */}
                  <button
                    onClick={generateMarketingImage}
                    disabled={generatingImage || !productImage}
                    className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg hover:from-green-700 hover:to-blue-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-lg"
                  >
                    {generatingImage ? (
                      <>
                        <Clock className="w-6 h-6 mr-2 animate-spin" />
                        Génération...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-6 h-6 mr-2" />
                        Générer Affiche Marketing
                      </>
                    )}
                  </button>
                </div>
                
                {/* RIGHT: Résultat */}
                <div>
                  <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-4">
                    <Star className="w-6 h-6 text-orange-500" />
                    Résultat
                  </h3>
                  
                  {/* Barre progression image */}
                  {generatingImage && imageJobStatus && (
                    <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-8 border-2 border-green-200">
                      
                      <div className="flex items-center justify-center mb-6">
                        <div className="relative">
                          <div className="absolute inset-0 rounded-full bg-green-400 opacity-30 animate-ping"></div>
                          <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center animate-spin">
                            <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center">
                              <ImageIcon className="w-10 h-10 text-green-600" />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <h4 className="text-2xl font-bold text-center text-gray-800 mb-2">
                        🎨 Création en cours...
                      </h4>
                      
                      <p className="text-center text-gray-600 mb-6">
                        TeckiA génère votre affiche marketing professionnelle
                      </p>
                      
                      <div className="relative w-full h-4 bg-gray-200 rounded-full overflow-hidden mb-4">
                        <div className="absolute inset-0 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500 animate-pulse"
                          style={{
                            width: '100%',
                            animation: 'progress 2s ease-in-out infinite'
                          }}>
                        </div>
                        
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
                          style={{
                            animation: 'shimmer 2s infinite'
                          }}>
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-700">
                          {imageJobStatus.status === 'queued' && '⏳ En file d\'attente...'}
                          {imageJobStatus.status === 'queuing' && '⏳ Préparation...'}
                          {imageJobStatus.status === 'generating' && '✨ Génération magique...'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Cela prend généralement 10-15 secondes
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-center gap-2 mt-4">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-bounce" style={{animationDelay: '0s'}}></div>
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        <div className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" style={{animationDelay: '0.4s'}}></div>
                      </div>
                    </div>
                  )}
                  
                  {/* Image générée */}
                  {generatedImage && !generatingImage && (
                    <div>
                      <div 
                        className="bg-black rounded-xl overflow-hidden shadow-2xl mb-4 cursor-pointer group relative border-4 border-green-400"
                        onClick={() => setZoomedImage(generatedImage)}
                      >
                        <img
                          src={generatedImage}
                          alt="Généré"
                          className="w-full"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition flex items-center justify-center">
                          <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition" />
                        </div>
                        
                        <div className="absolute top-4 right-4 px-4 py-2 bg-green-500 text-white rounded-full font-bold flex items-center gap-2">
                          <CheckCircle className="w-5 h-5" />
                          Terminé !
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <a 
                          href={generatedImage}
                          download
                          className="px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition font-semibold flex items-center justify-center shadow-lg"
                        >
                          <Download className="w-5 h-5 mr-2" />
                          Télécharger
                        </a>
                        
                        <button
                          onClick={() => openVideoPromptModal(generatedImage)}
                          disabled={generatingVideo}
                          className="px-4 py-3 bg-gradient-to-r from-pink-600 to-orange-600 text-white rounded-lg hover:from-pink-700 hover:to-orange-700 transition font-semibold flex items-center justify-center disabled:opacity-50 shadow-lg"
                        >
                          <Film className="w-5 h-5 mr-2" />
                          → Vidéo
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* ✅ LOADING VIDÉO (EN BAS DE L'IMAGE) */}
                  {generatingVideo && videoJobStatus && (
                    <div className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-xl p-8 border-2 border-pink-200 mt-4">
                      <div className="flex items-center justify-center mb-4">
                        <div className="relative">
                          <div className="absolute inset-0 rounded-full bg-pink-400 opacity-30 animate-ping"></div>
                          <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center animate-spin">
                            <Film className="w-10 h-10 text-white" />
                          </div>
                        </div>
                      </div>
                      
                      <h4 className="text-xl font-bold text-center text-gray-800 mb-2">
                        🎬 Création vidéo par TeckiA...
                      </h4>
                      
                      <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-4">
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-500 via-orange-500 to-yellow-500 animate-pulse"></div>
                      </div>
                      
                      <p className="text-sm text-center text-gray-600">
                        ⏱️ Durée estimée: 60-90 secondes
                      </p>
                    </div>
                  )}
                  
                  {/* ✅ VIDÉO GÉNÉRÉE (EN BAS) */}
                  {generatedVideo && !generatingVideo && (
                    <div className="mt-4">
                      <div className="bg-black rounded-xl overflow-hidden shadow-2xl mb-4 border-4 border-pink-400">
                        <video
                          controls
                          className="w-full"
                          src={generatedVideo}
                        />
                      </div>
                      
                      <div className="flex gap-3">
                        <a 
                          href={generatedVideo}
                          download
                          className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition font-semibold flex items-center justify-center shadow-lg"
                        >
                          <Download className="w-5 h-5 mr-2" />
                          Télécharger Vidéo
                        </a>
                        
                        <button
                          onClick={reset}
                          className="px-4 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 transition font-semibold flex items-center justify-center shadow-lg"
                        >
                          <Plus className="w-5 h-5 mr-2" />
                          Nouveau
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* État initial */}
                  {!generatingImage && !generatedImage && !generatingVideo && !generatedVideo && (
                    <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-12 text-center border-2 border-dashed border-green-300">
                      <div className="relative inline-block mb-4">
                        <ImageIcon className="w-24 h-24 text-green-300" />
                        <Sparkles className="w-8 h-8 text-blue-400 absolute -top-2 -right-2 animate-pulse" />
                      </div>
                      
                      <h4 className="text-2xl font-bold text-gray-700 mb-2">
                        Votre affiche apparaîtra ici
                      </h4>
                      <p className="text-gray-500">
                        Configurez et générez votre affiche marketing professionnelle
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* ONGLET MES CRÉATIONS */}
          {activeTab === 'my-creations' && (
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800 flex items-center">
                  <Layers className="w-6 h-6 mr-2 text-purple-600" />
                  Mes Créations
                </h3>
                
                <button
                  onClick={loadMyCreations}
                  disabled={loadingCreations}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 disabled:opacity-50"
                >
                  {loadingCreations ? (
                    <>
                      <Clock className="w-4 h-4 animate-spin" />
                      Chargement...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Actualiser
                    </>
                  )}
                </button>
              </div>
              
              {loadingCreations ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Chargement...</p>
                </div>
              ) : (
                <div className="space-y-8">
                  
                  {/* Images */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                      <ImageIcon className="w-5 h-5 mr-2 text-purple-600" />
                      Images ({myImages.length}/{limits.max_images})
                    </h4>
                    
                    {myImages.length === 0 ? (
                      <div className="text-center py-8 bg-purple-50 rounded-xl">
                        <ImageIcon className="w-12 h-12 text-purple-300 mx-auto mb-2" />
                        <p className="text-gray-500">Aucune image générée</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {myImages.map((img) => (
                          <div key={img.job_id} className="bg-white rounded-lg shadow hover:shadow-lg transition overflow-hidden">
                            <img
                              src={normalizeImageUrl(img.image_url)}
                              alt="Généré"
                              className="w-full aspect-square object-cover cursor-pointer"
                              onClick={() => setZoomedImage(normalizeImageUrl(img.image_url))}
                            />
                            
                            <div className="p-3">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className="inline-block px-2 py-1 rounded text-xs font-bold bg-green-100 text-green-800">
                                  🍌 TeckiA
                                </span>
                                
                                {!img.show_person && (
                                  <span className="inline-block px-2 py-1 rounded text-xs font-bold bg-orange-100 text-orange-800">
                                    📦 Seul
                                  </span>
                                )}
                              </div>
                              
                              <p className="text-xs text-gray-500 mb-2">
                                {new Date(img.created_at * 1000).toLocaleDateString('fr-FR')}
                              </p>
                              
                              <div className="grid grid-cols-3 gap-2">
                                <a 
                                  href={normalizeImageUrl(img.image_url)}
                                  download
                                  className="px-2 py-2 bg-green-600 text-white rounded text-xs font-medium text-center hover:bg-green-700 transition"
                                  title="Télécharger"
                                >
                                  <Download className="w-3 h-3 inline" />
                                </a>
                                
                                <button
                                  onClick={() => openVideoPromptModal(normalizeImageUrl(img.image_url))}
                                  disabled={generatingVideo}
                                  className="px-2 py-2 bg-pink-600 text-white rounded text-xs font-medium hover:bg-pink-700 transition disabled:opacity-50"
                                  title="Générer vidéo"
                                >
                                  <Film className="w-3 h-3 inline" />
                                </button>
                                
                                <button
                                  onClick={() => deleteImage(img)}
                                  className="px-2 py-2 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition"
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-3 h-3 inline" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Vidéos */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                      <Video className="w-5 h-5 mr-2 text-pink-600" />
                      Vidéos ({myVideos.length}/{limits.max_videos})
                    </h4>
                    
                    {myVideos.length === 0 ? (
                      <div className="text-center py-8 bg-pink-50 rounded-xl">
                        <Video className="w-12 h-12 text-pink-300 mx-auto mb-2" />
                        <p className="text-gray-500">Aucune vidéo générée</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {myVideos.map((vid) => (
                          <div key={vid.job_id} className="bg-white rounded-lg shadow hover:shadow-lg transition overflow-hidden">
                            <video
                              controls
                              className="w-full aspect-video"
                              src={normalizeImageUrl(vid.video_url)}
                            />
                            
                            <div className="p-3">
                              <span className="inline-block px-2 py-1 rounded text-xs font-bold bg-pink-100 text-pink-800 mb-2">
                                🎬 TeckiA Video
                              </span>
                              
                              <p className="text-xs text-gray-500 mb-2">
                                {new Date(vid.created_at * 1000).toLocaleDateString('fr-FR')}
                              </p>
                              
                              <div className="grid grid-cols-2 gap-2">
                                <a
                                  href={normalizeImageUrl(vid.video_url)}
                                  download
                                  className="px-3 py-2 bg-green-600 text-white rounded text-xs font-medium text-center hover:bg-green-700 transition"
                                >
                                  <Download className="w-3 h-3 inline mr-1" />
                                  Télécharger
                                </a>
                                
                                <button
                                  onClick={() => deleteVideo(vid)}
                                  className="px-3 py-2 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition"
                                >
                                  <Trash2 className="w-3 h-3 inline mr-1" />
                                  Supprimer
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketingStudio;